'use client';

import LensInfo from '@/app/_lens-components/components/LensInfo';
import {
  LENS_INDEX_COMPONENT,
  PRESCRIPTION_TYPE_COMPONENT,
} from '@/app/_lens-components/constants';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { processClassName } from '@/app/_lens-components/style/responsive';
import Button from '@/components/Button';
import Popup from '@/components/Popup';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import { useStore } from '@/providers/app-store-provider';
import { EPrescriptionType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CollapseArrowUpOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useState } from 'react';

interface FooterProps {
  onNextStep: () => void;
  isFrameProcess: boolean;
}

export default function Footer({ onNextStep, isFrameProcess }: FooterProps) {
  const t = useTranslations('lens-process');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const [btnShow, setBtnShow] = useState(true);
  const currency = useStore((state) => state.currency);
  const waiMode = useStore((state) => state.waiMode);
  const [showLensInfoPopup, setShowLensInfoPopup] = useState(false);
  const [popupContainer, setPopupContainer] = useState<HTMLElement | null>(null);
  const {
    lensGoodsItem,
    nowStep,
    nowComponent,
    btnDisable,
    addToCartLoading,
    stepBtnText,
    totalPrice,
    selectedColorItem,
    prescriptionType,
    selectedLensIndexItem,
    showChooseColor,
    setShowChooseColor,
    setStepBtnText,
  } = useLensProcessStore();

  useEffect(() => {
    if (isFrameProcess) {
      setBtnShow(true);
    } else {
      const hideBtn =
        [PRESCRIPTION_TYPE_COMPONENT].includes(nowComponent) ||
        ([EPrescriptionType.PROGRESSIVE].includes(prescriptionType as EPrescriptionType) &&
          [LENS_INDEX_COMPONENT].includes(nowComponent) &&
          !selectedLensIndexItem);
      setBtnShow(!hideBtn);
    }
  }, [nowComponent, isFrameProcess, prescriptionType, selectedLensIndexItem]);

  useEffect(() => {
    setPopupContainer(document.getElementById('right-box-content'));
    setStepBtnText(t('next'));
  }, []);

  return (
    <>
      {
        <div
          id="glass-process-bottom-area"
          className="fixed z-100 w-full box-border p-0 max-xl:px-[16px] h-[69px] xl:h-[87px] bottom-0 left-0 right-0 max-xl:rounded-t-[12px] shadow-[0px_-2px_10px_0px_rgba(204,204,204,0.5)] bg-white flex justify-between items-center"
        >
          {/* 商品区域 如果是第一步&&selectlens 居中展示 没有下一步按钮 */}
          {
            <>
              {/* 商品区域 */}
              <div
                className={`flex xl:hidden justify-start items-center w-full ${!btnShow && nowStep === 3 ? 'no-botton' : ''}`}
                onClick={() => {
                  if (showChooseColor) {
                    setShowChooseColor(false);
                  }
                  setShowLensInfoPopup(!showLensInfoPopup);
                }}
              >
                <div className="w-[60px] h-[30px]">
                  <ProductImageWithLenses
                    className="w-full"
                    imgUrl={lensGoodsItem?.product?.img || ''}
                    blendFirst
                    product={{
                      background_image_of_mirror_leg:
                        lensGoodsItem?.product?.image?.background_image_of_mirror_leg?.[0] || '',
                      frame_background_image:
                        lensGoodsItem?.product?.image?.frame_background_image?.[0] || '',
                    }}
                    lens={selectedColorItem}
                  />
                </div>
                <div className="flex flex-col justify-center items-start ml-[12px]">
                  <div className="max-w-[90px] overflow-hidden whitespace-nowrap text-ellipsis text-[#333333] text-xs font-normal">
                    {lensGoodsItem?.product?.short_name || ''}
                  </div>
                  <div className="flex justify-start items-start flex-col mt-[4px]">
                    <span className="notranslate f14 font-bb">
                      {formatPriceNumberWithSymbol(totalPrice.sell_price, currency)}
                    </span>
                  </div>
                </div>
                <span
                  className={`material-icons ml-[12px] text-[#89909a] font-normal ${showLensInfoPopup ? 'rotate-180' : ''}`}
                >
                  <CollapseArrowUpOutlined />
                </span>
              </div>

              <div className={`left-part ${processClassName.leftPart}`}>
                <div className={`left-side ${processClassName.leftPartSide}`} />
                <div className={`left-box ${processClassName.leftPartBox}`}>
                  <p className="f14 font-bs">{t('subtotal')}</p>
                  <div className="flex justify-between items-center">
                    {totalPrice.price !== totalPrice.sell_price && (
                      <del
                        className={`f14 ${waiMode ? '' : 'text-[#93969e]'} line-through`}
                        aria-label={
                          tPriceA11y('srOriginal') +
                          formatPriceNumberWithSymbol(totalPrice.price, currency)
                        }
                      >
                        {formatPriceNumberWithSymbol(totalPrice.price, currency)}
                      </del>
                    )}
                    <p
                      className="text-[28px] font-bb ml-[7px]"
                      aria-label={
                        tPriceA11y('srCurrent') +
                        formatPriceNumberWithSymbol(totalPrice.sell_price, currency)
                      }
                    >
                      {formatPriceNumberWithSymbol(totalPrice.sell_price, currency)}
                    </p>
                  </div>
                </div>
              </div>

              {/* 按钮区域 */}
              <div className={`right-part ${processClassName.rightPart}`}>
                <div className={processClassName.rightPartContent}>
                  <div className={`right-box ${processClassName.rightPartBox} flex justify-end`}>
                    {btnShow && stepBtnText && (
                      <div className="notranslate">
                        <Button
                          variant="contained"
                          color="primary"
                          className="!min-w-[136px] h-[40px] xl:w-[294px] xl:h-[58px] !text-[14px] xl:!text-[20px] !text-white !font-bs xl:!font-bb rounded-[20px] xl:rounded-[2px]"
                          disabled={btnDisable}
                          loading={addToCartLoading}
                          onClick={onNextStep}
                        >
                          {stepBtnText}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className={`right-side ${processClassName.rightPartSide}`} />
                </div>
              </div>
            </>
          }
        </div>
      }
      {popupContainer && (
        <Popup
          open={showLensInfoPopup}
          className="price-desc-diag-area"
          bodyClassName="!pt-[0]"
          container={popupContainer}
          title="&nbsp;"
          showDivider={false}
          zIndex={90}
          round
          position="bottom"
          onClose={() => setShowLensInfoPopup(false)}
        >
          <LensInfo isFrameProcess={isFrameProcess} />
        </Popup>
      )}
    </>
  );
}
